import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { createCheckout, getPaymentsConfig, checkAccess, getSettings } from "@/lib/api";

const RED = "#e11d2a";

export default function Auth({ mode = "login" }) {
  const [tab, setTab] = useState(mode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("19,99 €");
  const [access, setAccess] = useState(null); // null | "grant" | "registered" | "none"
  const [accessType, setAccessType] = useState(null); // "lifetime" | "subscriber"
  const [banners, setBanners] = useState({
    auth_banner_new: "Bienvenue au Dōjō ! Un accès t'a été offert 🎉 Crée ton mot de passe pour entrer.",
    auth_banner_lifetime: "👑 Tu as un accès à VIE au Dōjō ! Crée ton mot de passe pour entrer — pour toujours.",
    auth_banner_return: "Content de te revoir. Entre ton mot de passe pour accéder au Dōjō.",
  });
  const { login, register, startGoogleLogin, isAuthenticated } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/hub";

  useEffect(() => {
    if (isAuthenticated) nav("/hub", { replace: true });
  }, [isAuthenticated, nav]);

  useEffect(() => {
    getPaymentsConfig().then((c) => {
      const p = c?.dojo_monthly?.amount;
      if (p) setPrice((p / 100).toFixed(2).replace(".", ",") + " €");
    }).catch(() => {});
    getSettings().then((s) => setBanners((b) => ({
      auth_banner_new: s.auth_banner_new || b.auth_banner_new,
      auth_banner_lifetime: s.auth_banner_lifetime || b.auth_banner_lifetime,
      auth_banner_return: s.auth_banner_return || b.auth_banner_return,
    }))).catch(() => {});
  }, []);

  const resetCheck = () => { setAccess(null); setAccessType(null); setError(""); setPassword(""); setName(""); };

  // Onglet Connexion — Étape 1 : on vérifie l'email (compte existant ou accès offert).
  const detectAccess = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const r = await checkAccess(email.trim());
      setAccess(r.status);
      setAccessType(r.type || null);
    } catch {
      setError("Vérification impossible. Réessaie.");
    } finally { setBusy(false); }
  };

  // Abonnement obligatoire → démarre le paiement Stripe (onglet Rejoindre)
  const startSubscription = async () => {
    setError(""); setBusy(true);
    try {
      const { checkout_url } = await createCheckout("dojo_monthly");
      window.location.href = checkout_url;
    } catch (err) {
      setError("Impossible de démarrer le paiement. Réessaie.");
      setBusy(false);
    }
  };

  // Connexion classique (compte existant) OU création via accès offert (email + mot de passe)
  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      let u;
      if (access === "grant") u = await register({ email: email.trim(), password, name: name.trim() || undefined });
      else u = await login(email.trim(), password);
      nav(u && !u.profile_completed ? "/profil" : from, { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) {
        setError("Aucun accès trouvé pour cet email. Prends d'abord l'abonnement (onglet Rejoindre).");
      } else {
        const msg = err?.response?.data?.detail || "Une erreur est survenue.";
        setError(typeof msg === "string" ? msg : "Une erreur est survenue.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brandRow}>
          <span style={styles.kanji}>天</span>
          <span style={styles.brand}>TENGOKU <span style={{ color: RED }}>DŌJŌ</span></span>
        </div>

        <div style={styles.tabs}>
          <button data-testid="auth-tab-login" onClick={() => { setTab("login"); resetCheck(); }}
            style={{ ...styles.tab, ...(tab === "login" ? styles.tabActive : {}) }}>Connexion</button>
          <button data-testid="auth-tab-register" onClick={() => { setTab("register"); setError(""); }}
            style={{ ...styles.tab, ...(tab === "register" ? styles.tabActive : {}) }}>Rejoindre</button>
        </div>

        {tab === "login" ? (
          access === null ? (
            // Étape 1 : email → vérification
            <>
              <p style={styles.lead}>Entre ton email pour continuer.</p>
              <button onClick={startGoogleLogin} style={styles.google} type="button" data-testid="auth-google-login">
                <GoogleIcon /> Continuer avec Google
              </button>
              <div style={styles.divider}><span style={styles.dividerLine} /><span style={styles.dividerText}>ou</span><span style={styles.dividerLine} /></div>
              <form onSubmit={detectAccess} style={styles.form}>
                <Field label="Email"><input style={styles.input} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@email.com" data-testid="auth-check-email" /></Field>
                {error && <div style={styles.error} data-testid="auth-error">{error}</div>}
                <button type="submit" disabled={busy} style={{ ...styles.submit, opacity: busy ? 0.6 : 1 }} data-testid="auth-check-btn">
                  {busy ? "Vérification…" : "Continuer"}
                </button>
              </form>
            </>
          ) : access === "registered" ? (
            // Compte existant → entrer le mot de passe
            <div data-testid="auth-registered">
              <div style={styles.returnBanner} data-testid="auth-banner-return">{banners.auth_banner_return}</div>
              <form onSubmit={submit} style={{ ...styles.form, marginTop: 16 }}>
                <Field label="Email"><input style={{ ...styles.input, background: "#f6f6f6", color: "#666" }} type="email" value={email} readOnly data-testid="auth-login-email" /></Field>
                <Field label="Mot de passe"><input style={styles.input} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="auth-password" autoFocus /></Field>
                {error && <div style={styles.error} data-testid="auth-error">{error}</div>}
                <button type="submit" disabled={busy} style={{ ...styles.submit, opacity: busy ? 0.6 : 1 }} data-testid="auth-submit">
                  {busy ? "Un instant…" : "Se connecter"}
                </button>
              </form>
              <button type="button" onClick={resetCheck} style={styles.changeEmail} data-testid="auth-change-email">← Utiliser un autre email</button>
            </div>
          ) : access === "grant" ? (
            // Accès offert, première fois → créer son mot de passe
            <div data-testid="auth-grant">
              {accessType === "lifetime"
                ? <div style={styles.lifetimeBanner} data-testid="auth-banner-lifetime">{banners.auth_banner_lifetime}</div>
                : <div style={styles.grantBanner} data-testid="auth-banner-new">{banners.auth_banner_new}</div>}
              <button onClick={startGoogleLogin} style={{ ...styles.google, marginTop: 16 }} type="button" data-testid="auth-grant-google">
                <GoogleIcon /> Créer mon compte avec Google
              </button>
              <div style={styles.divider}><span style={styles.dividerLine} /><span style={styles.dividerText}>ou avec un mot de passe</span><span style={styles.dividerLine} /></div>
              <form onSubmit={submit} style={styles.form}>
                <Field label="Email"><input style={{ ...styles.input, background: "#f6f6f6", color: "#666" }} type="email" value={email} readOnly data-testid="auth-grant-email" /></Field>
                <Field label="Ton nom"><input style={styles.input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Votre nom" data-testid="auth-grant-name" /></Field>
                <Field label="Crée ton mot de passe"><input style={styles.input} type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="6 caractères minimum" data-testid="auth-grant-password" /></Field>
                {error && <div style={styles.error} data-testid="auth-error">{error}</div>}
                <button type="submit" disabled={busy} style={{ ...styles.submit, opacity: busy ? 0.6 : 1 }} data-testid="auth-grant-submit">
                  {busy ? "Création…" : "Créer mon compte et entrer"}
                </button>
              </form>
              <button type="button" onClick={resetCheck} style={styles.changeEmail} data-testid="auth-change-email">← Ce n'est pas mon email</button>
            </div>
          ) : (
            // Ni compte ni accès → inviter à s'abonner
            <div data-testid="auth-nogrant">
              <div style={styles.infoBanner}>Aucun compte ni accès trouvé pour cet email. Rejoins le Dōjō pour créer ton accès.</div>
              <button onClick={() => { setTab("register"); setError(""); }} style={{ ...styles.submit, marginTop: 16 }} data-testid="auth-goto-register">Rejoindre le Dōjō →</button>
              <button type="button" onClick={resetCheck} style={styles.changeEmail} data-testid="auth-change-email">← Utiliser un autre email</button>
            </div>
          )
        ) : (
          // Onglet Rejoindre → abonnement direct
          <div data-testid="auth-register-pane">
            <div style={{ ...styles.planCard }} data-testid="auth-plan-card">
              <div style={styles.planTag}>ACCÈS COMPLET AU DŌJŌ</div>
              <div style={styles.planPrice}><span style={styles.planNum}>{price}</span><span style={styles.planPer}>/ mois</span></div>
              <ul style={styles.planList}>
                <li>✓ Toutes les formations de l'École</li>
                <li>✓ La Bibliothèque complète</li>
                <li>✓ La Communauté Discord privée</li>
                <li>✓ Une séance Renzoku offerte</li>
                <li>✓ Résiliable à tout moment</li>
              </ul>
              <button style={{ ...styles.submit, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={startSubscription} data-testid="auth-subscribe-btn">
                {busy ? "Ouverture du paiement…" : `M'abonner — ${price}/mois`}
              </button>
              <p style={styles.secure}>🔒 Paiement sécurisé par Stripe</p>
            </div>
            {error && <div style={styles.error} data-testid="auth-error">{error}</div>}
            <p style={styles.altHint}>Déjà membre ou accès offert ? <button type="button" style={styles.linkBtn} onClick={() => { setTab("login"); resetCheck(); }} data-testid="auth-goto-login">Connexion</button></p>
          </div>
        )}

        <Link to="/" style={styles.back}>← Retour</Link>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={styles.field}><label style={styles.label}>{label}</label>{children}</div>
);
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" style={{ marginRight: 10 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);

const styles = {
  page: { minHeight: "100vh", width: "100%", background: "#ffffff", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, fontFamily: "'Inter', system-ui, sans-serif" },
  card: { width: "100%", maxWidth: 440, background: "#fff", border: "1px solid #ededed", borderRadius: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.08)", padding: "36px 32px" },
  brandRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 12, marginBottom: 24 },
  kanji: { fontSize: 34, fontWeight: 700, color: "#111" },
  brand: { fontSize: 20, fontWeight: 800, letterSpacing: 3, color: "#111" },
  tabs: { display: "flex", gap: 8, background: "#f4f4f4", padding: 4, borderRadius: 12, marginBottom: 22 },
  tab: { flex: 1, padding: "10px 0", borderRadius: 9, border: "none", background: "transparent", color: "#666", fontWeight: 700, cursor: "pointer", fontSize: 14 },
  tabActive: { background: "#fff", color: RED, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
  google: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0", border: "1.5px solid #e2e2e2", borderRadius: 10, background: "#fff", color: "#222", fontWeight: 600, cursor: "pointer", fontSize: 14 },
  divider: { display: "flex", alignItems: "center", gap: 12, margin: "20px 0" },
  dividerLine: { flex: 1, height: 1, background: "#eee" },
  dividerText: { fontSize: 12, color: "#aaa" },
  form: { display: "flex", flexDirection: "column", gap: 16 },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: "#444" },
  input: { padding: "12px 14px", border: "1.5px solid #e0e0e0", borderRadius: 10, fontSize: 14, outline: "none", color: "#111" },
  error: { background: `${RED}11`, border: `1px solid ${RED}44`, color: RED, padding: "10px 12px", borderRadius: 10, fontSize: 13.5, fontWeight: 500, marginTop: 14 },
  submit: { padding: "13px 0", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 15, boxShadow: `0 6px 18px ${RED}44`, marginTop: 4, width: "100%" },
  submitGhost: { padding: "13px 0", borderRadius: 10, border: "1.5px solid #ddd", background: "#fff", color: "#222", fontWeight: 800, cursor: "pointer", fontSize: 14, width: "100%" },
  planCard: { border: `2px solid ${RED}`, borderRadius: 18, padding: 24, textAlign: "center", boxShadow: `0 14px 40px ${RED}18` },
  planTag: { color: RED, fontWeight: 800, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" },
  planPrice: { margin: "10px 0 14px" },
  planNum: { fontSize: 40, fontWeight: 900 },
  planPer: { fontSize: 15, color: "#888", marginLeft: 6 },
  planList: { listStyle: "none", padding: 0, margin: "0 0 20px", textAlign: "left", display: "flex", flexDirection: "column", gap: 9, color: "#333", fontSize: 14 },
  secure: { fontSize: 12, color: "#aaa", marginTop: 12, lineHeight: 1.5 },
  lead: { textAlign: "center", color: "#555", fontSize: 14.5, margin: "0 0 18px", lineHeight: 1.5 },
  grantBanner: { background: "#e8f7ec", color: "#1a7f37", border: "1px solid #b7e4c4", padding: "13px 16px", borderRadius: 12, fontWeight: 700, fontSize: 14.5, textAlign: "center", lineHeight: 1.5 },
  returnBanner: { background: "#eef2ff", color: "#3730a3", border: "1px solid #c7d2fe", padding: "13px 16px", borderRadius: 12, fontWeight: 700, fontSize: 14.5, textAlign: "center", lineHeight: 1.5 },
  lifetimeBanner: { background: "linear-gradient(135deg, #fff6da, #ffeab0)", color: "#8a5a00", border: "1px solid #e9c86b", padding: "13px 16px", borderRadius: 12, fontWeight: 800, fontSize: 14.5, textAlign: "center", lineHeight: 1.5, boxShadow: "0 6px 20px rgba(201,150,40,0.18)" },
  altHint: { textAlign: "center", fontSize: 13, color: "#888", marginTop: 16 },
  linkBtn: { background: "none", border: "none", color: RED, fontWeight: 800, cursor: "pointer", fontSize: 13, padding: 0 },
  infoBanner: { background: "#fff8e6", color: "#8a6100", border: "1px solid #f2e0ad", padding: "12px 15px", borderRadius: 12, fontWeight: 600, fontSize: 14, textAlign: "center", lineHeight: 1.45 },
  changeEmail: { display: "block", width: "100%", textAlign: "center", marginTop: 14, background: "none", border: "none", color: "#888", fontSize: 13, fontWeight: 600, cursor: "pointer" },
  grantToggle: { width: "100%", marginTop: 18, padding: "11px 0", borderRadius: 10, border: "1px dashed #d5d5d5", background: "#fafafa", color: "#666", fontWeight: 600, cursor: "pointer", fontSize: 13 },
  back: { display: "block", textAlign: "center", marginTop: 20, color: "#888", fontSize: 13, textDecoration: "none" },
};
